import { useQuery } from '@apollo/client';
import React, { createContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate } from 'react-router-dom';
import GET_CURRENT_TENANT from 'api/queries/GET_CURRENT_TENANT';
import Loader from 'atoms/Loader/Loader';
import { fetchUserUnreadCounts } from 'store/inboxSlice';
import { fetchCurrentTennant, fetchOrganizations, fetchUserProfile } from 'store/userSlice';
export const TenantContext = createContext({});
/**
 * Why a signed-in user without a usable organization has none yet: one is still being set up
 * (PROVISIONING, or AWAITING_DATA while its data is copied), or setting it up failed.
 * Undefined when neither applies - the user really belongs to no organization.
 */
export function organizationStatusOf(me) {
    var _a, _b;
    const statuses = ((_b = (_a = me === null || me === void 0 ? void 0 : me.tenants) === null || _a === void 0 ? void 0 : _a.edges) !== null && _b !== void 0 ? _b : []).map((edge) => { var _a; return (_a = edge === null || edge === void 0 ? void 0 : edge.node) === null || _a === void 0 ? void 0 : _a.provisioningStatus; });
    if (statuses.some((status) => status === 'PROVISIONING' || status === 'AWAITING_DATA')) {
        return 'preparing';
    }
    if (statuses.includes('FAILED')) {
        return 'failed';
    }
    return undefined;
}
function OrganizationAccessRoute({ children }) {
    var _a;
    const dispatch = useDispatch();
    const { data, loading } = useQuery(GET_CURRENT_TENANT, {
        fetchPolicy: 'network-only',
    });
    useEffect(() => {
        dispatch(fetchUserProfile());
        dispatch(fetchCurrentTennant());
        // Organization list + role for the switcher and the owner-only navigation.
        dispatch(fetchOrganizations());
        dispatch(fetchUserUnreadCounts());
    }, [dispatch]);
    if (loading) {
        return React.createElement(Loader, { fullViewport: true });
    }
    if ((_a = data === null || data === void 0 ? void 0 : data.me) === null || _a === void 0 ? void 0 : _a.currentTenant) {
        return React.createElement(TenantContext.Provider, { value: data }, children);
    }
    // Both a failed query and a genuinely empty result land here.
    //
    // This used to navigate to "/login" on error, but no such route exists - sign-in is
    // Keycloak's hosted page, reached by keycloak-js when there is no valid token. That
    // rendered a blank screen, which is what a transient backend error looked like to a user
    // who had just registered. The no-organization screen at least explains itself and offers
    // a way out, and a reload retries.
    return (React.createElement(Navigate, { to: "/no-organization", replace: true, state: { organizationStatus: organizationStatusOf(data === null || data === void 0 ? void 0 : data.me) } }));
}
export default OrganizationAccessRoute;
